/**
 * TriggerAttribution — one-line "Triggered by" badge on run-history rows.
 *
 * Renders when a run was fired by a TriggerRule (Option A, ADR 0018 Q5).
 * Heuristic: `buildMetadata.repoIdentifier` is present. Manual runs and
 * Option-B CI-YAML runs that don't carry `buildMetadata` return null.
 *
 * Three shapes are rendered, picked off the buildMetadata payload:
 *
 *   [Deploy pill] deploy to `environment` of `repo`  ·  sha abcd123
 *   [PR pill]     PR #N on `repo`  ·  sha abcd123
 *   [Push pill]   push to `branch` of `repo`  ·  sha abcd123
 *
 * Pick order:
 *
 *   1. `environment` set (or `environmentUrl` set with no env name) →
 *      Deploy pill. New runs fired by the deployment_status webhook
 *      branch (#1173 / meta #1172) land here. The dispatcher stamps
 *      both fields on `buildMetadata` so the surface here can tell
 *      "this was a deploy" without re-reading the rule.
 *   2. `pullRequestNumber` set → PR pill. Retired event type post
 *      #1173, but pre-existing run rows still carry the key via the
 *      open buildMetadata index signature and continue to render
 *      correctly for run-history backreads.
 *   3. Else → Push pill (also a legacy shape). Same backread story.
 *
 * Sits at `components/` (not `pages/`) so it can be tested in isolation
 * without pulling in the full Results page module.
 */
import { GitBranch } from 'lucide-react'
import type { TestRun } from '@/types'

type BuildMetadata = NonNullable<TestRun['buildMetadata']>

interface TriggerAttributionProps {
  buildMetadata?: BuildMetadata
}

export function TriggerAttribution({ buildMetadata }: TriggerAttributionProps) {
  if (!buildMetadata?.repoIdentifier) return null

  const {
    repoIdentifier,
    branch,
    sha,
    pullRequestNumber,
    environment,
    environmentUrl,
  } = buildMetadata
  const isDeploy = environment != null || environmentUrl != null
  const isPr = !isDeploy && pullRequestNumber != null

  let pillClass: string
  let pillLabel: string
  let description: string
  if (isDeploy) {
    // Purple matches the editor + Triggers list Deploy pill, so the
    // run-history surface reads consistently with the rule editor —
    // one Deploy color across the product.
    pillClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
    pillLabel = 'Deploy'
    description = `deploy${
      environment ? ` to ${environment}` : ''
    } on ${repoIdentifier}`
  } else if (isPr) {
    pillClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    pillLabel = 'PR'
    description = `PR #${pullRequestNumber} on ${repoIdentifier}`
  } else {
    pillClass = 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
    pillLabel = 'Push'
    description = `push${branch ? ` to ${branch}` : ''} on ${repoIdentifier}`
  }

  const shortSha = sha ? sha.slice(0, 7) : null

  return (
    <div className="mt-1.5 pl-6 flex items-center gap-1.5 text-[10px] text-muted-foreground">
      <span
        className={`rounded px-1 py-0.5 font-bold uppercase tracking-wider ${pillClass}`}
      >
        {pillLabel}
      </span>
      <GitBranch className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">
        {description}
        {shortSha && (
          <>
            {' · '}
            <span className="font-mono">{shortSha}</span>
          </>
        )}
      </span>
    </div>
  )
}
